import { apiRequest } from 'api/apiClient';
import {
  buildPageModuleContentPayload,
  buildPageModuleName,
  buildPageModuleType,
} from '../utils/pageModulePayload';

const createRegion = async (data) => {
  const response = await apiRequest('POST', '/v1/regions', data);
  return response;
};

const updateRegion = async ({ uid, ...data }) => {
  const response = await apiRequest('PATCH', `/v1/regions/${uid}`, data);
  return response;
};

const deleteRegion = async (uid) => {
  const response = await apiRequest('DELETE', `/v1/regions/${uid}`);
  return response;
};

const postShopifyRegionIntegration = async (data) => {
  const response = await apiRequest('POST', 'v1/integrations/setup/', data);
  return response;
};

const subscribeToRegionPlan = async ({ integrationId, plan_slug }) => {
  const response = await apiRequest('POST', `/v1/integrations/${integrationId}/subscribe/`, {
    plan_slug,
  });
  return response;
};

const cancelRegionSubscription = async ({ integrationId }) => {
  const response = await apiRequest(
    'POST',
    `/v1/integrations/${integrationId}/cancel-subscription/`,
  );
  return response;
};

const addHowToHubModule = async ({ regionUid, module_type, name, description, content }) => {
  const response = await apiRequest('POST', `/v1/regions/${regionUid}/how-to-hub/modules`, {
    module_type,
    name,
    description,
    ...content,
  });
  return response;
};

const addRegionBanner = async ({ regionUid, image_id, url, name }) => {
  const response = await apiRequest('POST', `/v1/regions/${regionUid}/banners`, {
    image_id,
    url,
    name,
  });
  return response;
};

const updateRegionBanner = async ({ regionUid, bannerUid, image_id, url, name }) => {
  const response = await apiRequest('PATCH', `/v1/regions/${regionUid}/banners/${bannerUid}`, {
    ...(image_id !== undefined ? { image_id } : {}),
    ...(url !== undefined ? { url } : {}),
    ...(name !== undefined ? { name } : {}),
  });
  return response;
};

const addRegionVideo = async ({ regionUid, video_url, video_file_id, video_file_url, name }) => {
  const response = await apiRequest('POST', `/v1/regions/${regionUid}/videos`, {
    video_url,
    video_file_id,
    video_file_url,
    name,
  });
  return response;
};

const updateRegionVideo = async ({
  regionUid,
  videoUid,
  name,
  video_url,
  video_file_id,
  video_file_url,
}) => {
  const response = await apiRequest('PATCH', `/v1/regions/${regionUid}/videos/${videoUid}`, {
    ...(name !== undefined ? { name } : {}),
    ...(video_url !== undefined ? { video_url } : {}),
    ...(video_file_id !== undefined ? { video_file_id } : {}),
    ...(video_file_url !== undefined ? { video_file_url } : {}),
  });
  return response;
};

const addRegionPageModule = async ({ regionUid, moduleType, name, content }) => {
  const transformedContent = buildPageModuleContentPayload(moduleType, content);
  const moduleName = buildPageModuleName(moduleType, name);
  const apiModuleType = buildPageModuleType(moduleType);

  const response = await apiRequest('POST', `/v1/regions/${regionUid}/modules`, {
    module_type: apiModuleType,
    name: moduleName,
    ...transformedContent,
  });
  return response;
};

const updateRegionPageModule = async ({ regionUid, moduleUid, moduleType, name, content }) => {
  const moduleName = buildPageModuleName(moduleType, name);
  const transformedContent =
    content === undefined ? {} : buildPageModuleContentPayload(moduleType, content);

  const response = await apiRequest('PATCH', `/v1/regions/${regionUid}/modules/${moduleUid}`, {
    name: moduleName,
    ...transformedContent,
  });
  return response;
};

const deleteRegionPageModule = async ({ regionUid, moduleUid }) => {
  const response = await apiRequest('DELETE', `/v1/regions/${regionUid}/modules/${moduleUid}`);
  return response;
};

const moveRegionPageModule = async ({ regionUid, uid, before, after }) => {
  const response = await apiRequest('PATCH', `/v1/regions/${regionUid}/modules/${uid}/move`, {
    ...(before ? { before } : {}),
    ...(after ? { after } : {}),
  });
  return response;
};

const deleteRegionBanner = async ({ regionUid, bannerUid }) => {
  const response = await apiRequest('DELETE', `/v1/regions/${regionUid}/banners/${bannerUid}`);
  return response;
};

const deleteRegionVideo = async ({ regionUid, videoUid }) => {
  const response = await apiRequest('DELETE', `/v1/regions/${regionUid}/videos/${videoUid}`);
  return response;
};

const deleteHowToHubModule = async ({ regionUid, moduleUid }) => {
  const response = await apiRequest(
    'DELETE',
    `/v1/regions/${regionUid}/how-to-hub/modules/${moduleUid}`,
  );
  return response;
};

const moveHowToHubModule = async ({ regionUid, uid, before, after }) => {
  const response = await apiRequest(
    'PATCH',
    `/v1/regions/${regionUid}/how-to-hub/modules/${uid}/move`,
    {
      ...(before ? { before } : {}),
      ...(after ? { after } : {}),
    },
  );
  return response;
};

const updateCheckoutConfiguration = async ({ uid, configuration }) => {
  const response = await apiRequest('PATCH', `/v1/checkout-configurations/${uid}`, configuration);
  return response;
};

const REGIONS_MUTATIONS = {
  CREATE_REGION: createRegion,
  UPDATE_REGION: updateRegion,
  POST_SHOPIFY_REGION_INTEGRATION: postShopifyRegionIntegration,
  DELETE_REGION: deleteRegion,
  SUBSCRIBE_TO_REGION_PLAN: subscribeToRegionPlan,
  CANCEL_REGION_SUBSCRIPTION: cancelRegionSubscription,
  ADD_HOW_TO_HUB_MODULE: addHowToHubModule,
  ADD_REGION_BANNER: addRegionBanner,
  UPDATE_REGION_BANNER: updateRegionBanner,
  ADD_REGION_VIDEO: addRegionVideo,
  UPDATE_REGION_VIDEO: updateRegionVideo,
  ADD_REGION_PAGE_MODULE: addRegionPageModule,
  UPDATE_REGION_PAGE_MODULE: updateRegionPageModule,
  DELETE_REGION_PAGE_MODULE: deleteRegionPageModule,
  DELETE_REGION_BANNER: deleteRegionBanner,
  DELETE_REGION_VIDEO: deleteRegionVideo,
  MOVE_REGION_PAGE_MODULE: moveRegionPageModule,
  DELETE_HOW_TO_HUB_MODULE: deleteHowToHubModule,
  MOVE_HOW_TO_HUB_MODULE: moveHowToHubModule,
  UPDATE_CHECKOUT_CONFIGURATION: updateCheckoutConfiguration,
};

export default REGIONS_MUTATIONS;
